import { useId, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@mds/shared/lib/api';
import { isChoiceType } from '@mds/shared/lib/form-answers';
import {
  FORM_QUESTION_TYPES,
  type FormQuestionSet,
  type FormQuestionType,
} from '@mds/shared/types/form-question';
import Select from '@mds/shared/ui/select';

import { useSession } from '@/auth/use-session';
import Button from '@/components/ui/button';
import Card from '@/components/ui/card';
import Chip from '@/components/ui/chip';
import EmptyState from '@/components/ui/empty-state';
import Field, { FieldError, inputCls, selectCls } from '@/components/ui/field';
import { SortableItem, SortableList } from '@/components/ui/sortable-list';
import {
  QUESTION_TYPE_LABELS,
  blankQuestion,
  blankSection,
  errorCode,
  questionCount,
  questionSetSchema,
  type QuestionDraft,
  type QuestionSetDraft,
  type SectionDraft,
} from '@/domain/form-builder';
import { useUnsaved } from '@/hooks/use-unsaved';
import { useValidation, type FieldBinder } from '@/lib/validation';

const TYPE_OPTIONS = FORM_QUESTION_TYPES.map((value) => ({
  value,
  label: QUESTION_TYPE_LABELS[value],
}));

/** "Section 2 · About you", or just "Section 2" before it has a title. */
const sectionName = (section: SectionDraft, index: number) =>
  `Section ${index + 1}${section.title.trim() ? ` · ${section.title.trim()}` : ''}`;

/** What every event form shares: its questions, and the version it was saved at. */
export interface BuiltForm extends FormQuestionSet {
  version: number;
  updatedAt: string | null;
}

/** Handed to a form's intro card, so its own switches edit the same draft. */
export interface IntroContext<Form, Draft> {
  loaded: Form;
  draft: Draft;
  change: (next: Draft) => void;
  readOnly: boolean;
  mayManage: boolean;
}

/** The message for a draft path once it should show — `useValidation().error`. */
type ErrorReader = (path: string) => string | undefined;

/**
 * One form's editor — an event's registration and feedback forms, and the booking feedback forms.
 *
 * The fields a form always asks are drawn as locked rows; everything else is optional sections
 * and questions. The whole form saves in one request against the version it was loaded at, so
 * a copy someone else saved over is refused rather than overwritten. Unsaved edits are reported
 * to the app, so a link away asks first. Save and Discard are drawn into `actionsSlot` — the
 * footer of the dialog the form sits in.
 */
export default function FormBuilder<Form extends BuiltForm, Draft extends QuestionSetDraft>({
  queryKey,
  load,
  save: replace,
  draftOf,
  changedCode,
  locked = () => false,
  noun,
  intro,
  alwaysAsked,
  questionsHint,
  savedNotice,
  saveProblem = () => null,
  onSaved,
  actionsSlot,
  managePermission = 'events.manage',
}: {
  queryKey: readonly unknown[];
  load: () => Promise<Form>;
  save: (draft: Draft, expectedVersion: number) => Promise<Form>;
  draftOf: (form: Form) => Draft;
  /** The API's code for "someone else saved first". */
  changedCode: string;
  /** True once the form can no longer change at all. */
  locked?: (form: Form) => boolean;
  /** "feedback form", "registration form". */
  noun: string;
  intro: (context: IntroContext<Form, Draft>) => ReactNode;
  /** The locked rows: what the form asks whatever its questions are. */
  alwaysAsked: (loaded: Form) => ReactNode;
  /** One sentence over the questions: where they appear, and what sections are for. */
  questionsHint?: string;
  savedNotice: (saved: Form) => string;
  /** Words for a refused save the builder does not explain itself, or null for the default. */
  saveProblem?: (error: unknown) => string | null;
  /** After a save lands — for whatever else on the page reads this form's state. */
  onSaved?: (saved: Form) => void;
  /** Where Save and Discard go; nothing is drawn until it mounts. */
  actionsSlot: HTMLElement | null;
  /** Who may edit: the event forms are `events.manage`, the booking forms `reservations.manage`. */
  managePermission?: string;
}) {
  const queryClient = useQueryClient();
  const { can } = useSession();
  const mayManage = can(managePermission);

  const form = useQuery({ queryKey, queryFn: load });

  const [draft, setDraft] = useState<Draft | null>(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /** Someone else saved first: this copy can only be thrown away and reloaded. */
  const [stale, setStale] = useState(false);
  const validation = useValidation(questionSetSchema, draft);
  useUnsaved(dirty);

  // Seeded once per loaded version, during render rather than in an effect so
  // the first paint already has it. An unsaved edit is never overwritten by a
  // background refetch.
  const [seededVersion, setSeededVersion] = useState<number | null>(null);
  if (form.data && !dirty && seededVersion !== form.data.version) {
    setSeededVersion(form.data.version);
    setDraft(draftOf(form.data));
    validation.reset();
  }

  const change = (next: Draft) => {
    setDraft(next);
    setDirty(true);
    setNotice(null);
  };

  const reload = async () => {
    setDirty(false);
    setStale(false);
    setProblem(null);
    // Back to the copy already loaded straight away; the refetch then brings
    // in whatever has changed since.
    if (form.data) {
      setDraft(draftOf(form.data));
      validation.reset();
    }
    const fresh = await form.refetch();
    if (fresh.data) {
      setSeededVersion(fresh.data.version);
      setDraft(draftOf(fresh.data));
      validation.reset();
    }
  };

  const save = async (loaded: Form, current: Draft) => {
    if (!validation.check()) return;
    setBusy(true);
    setProblem(null);
    try {
      const saved = await replace(current, loaded.version);
      queryClient.setQueryData(queryKey, saved);
      setSeededVersion(saved.version);
      setDraft(draftOf(saved));
      validation.reset();
      setDirty(false);
      setNotice(savedNotice(saved));
      onSaved?.(saved);
    } catch (e) {
      const changed = errorCode(e) === changedCode;
      setStale(changed);
      setProblem(
        changed
          ? `Someone else saved this ${noun} while you were editing. Reload to see their version. Your changes here will be lost.`
          : (saveProblem(e) ??
              (e instanceof ApiError ? e.message : 'That did not save. Try again.')),
      );
    } finally {
      setBusy(false);
    }
  };

  if (form.isPending) return <p className="m-0 text-base text-ink-muted">Loading the form…</p>;
  if (form.isError || !draft)
    return (
      <EmptyState
        title={`Could not load the ${noun}`}
        hint={
          form.error instanceof ApiError ? form.error.message : 'The server could not be reached.'
        }
      />
    );

  const loaded = form.data;
  const readOnly = !mayManage || locked(loaded);

  const actions = readOnly ? null : (
    <>
      <Button onClick={() => void save(loaded, draft)} disabled={busy || !dirty}>
        {busy ? 'Saving…' : 'Save form'}
      </Button>
      {dirty && (
        <Button variant="quiet" disabled={busy} onClick={() => void reload()}>
          Discard changes
        </Button>
      )}
    </>
  );

  return (
    <div className="flex flex-col gap-6">
      {intro({ loaded, draft, change, readOnly, mayManage })}

      {notice && (
        <p
          role="status"
          className="m-0 rounded-xl border border-brand-edge/35 bg-brand-wash px-4 py-3 text-base text-brand-strong"
        >
          {notice}
        </p>
      )}
      {problem && (
        <div
          role="alert"
          className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-danger-edge bg-danger-wash px-4 py-3 text-base text-danger-text"
        >
          <span>{problem}</span>
          {stale && (
            <Button variant="quiet" onClick={() => void reload()}>
              Reload
            </Button>
          )}
        </div>
      )}

      <Card title="Always asked">
        <ul className="m-0 flex list-none flex-col gap-2 p-0">{alwaysAsked(loaded)}</ul>
      </Card>

      <Card title="Your questions" action={<Chip>{questionCount(draft)} of 100</Chip>}>
        <QuestionSetEditor
          draft={draft}
          onChange={change}
          readOnly={readOnly}
          field={validation.field}
          error={validation.error}
          hint={questionsHint}
        />
      </Card>

      {actions && actionsSlot && createPortal(actions, actionsSlot)}
    </div>
  );
}

/** A field the form asks whatever its questions are, drawn so it cannot be edited. */
export function LockedRow({ label, detail }: { label: string; detail: string }) {
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-hairline bg-raised px-4 py-3">
      <span className="text-base font-semibold">{label}</span>
      <span className="font-mono text-sm text-ink-muted uppercase">{detail}</span>
    </li>
  );
}

/**
 * The questions of a form, edited in place: the loose ones first, then each section, with the
 * buttons that add more. Checked against `questionSetSchema` by whoever owns the draft — the
 * builder above, or the create-event dialog — which hands in its `field` and `error`.
 */
export function QuestionSetEditor<Draft extends QuestionSetDraft>({
  draft,
  onChange,
  readOnly,
  field,
  error,
  hint,
}: {
  draft: Draft;
  onChange: (next: Draft) => void;
  readOnly: boolean;
  field: FieldBinder;
  error: ErrorReader;
  /** One sentence over the questions: where they appear, and what sections are for. */
  hint?: string;
}) {
  const tooMany = error('count');
  const tooManySections = error('sections');
  return (
    <div className="flex flex-col gap-5">
      {hint && <p className="m-0 text-base text-ink-secondary">{hint}</p>}
      <QuestionList
        questions={draft.ungrouped}
        readOnly={readOnly}
        onChange={(ungrouped) => onChange({ ...draft, ungrouped })}
        empty={
          readOnly
            ? 'No questions outside a section.'
            : 'No questions yet. What is always asked above is enough on its own.'
        }
        path="ungrouped"
        field={field}
      />
      {tooMany && <FieldError>{tooMany}</FieldError>}

      <SortableList
        items={draft.sections}
        idOf={(section) => section.key}
        nameOf={sectionName}
        onReorder={(sections) => onChange({ ...draft, sections })}
      >
        {draft.sections.map((section, i) => (
          <SortableItem
            key={section.key}
            id={section.key}
            label={`Drag to reorder ${sectionName(section, i).toLowerCase()}`}
            disabled={readOnly}
          >
            {(handle) => (
              <SectionBlock
                section={section}
                index={i}
                handle={handle}
                readOnly={readOnly}
                onChange={(next) =>
                  onChange({
                    ...draft,
                    sections: draft.sections.map((s, at) => (at === i ? next : s)),
                  })
                }
                onRemove={() =>
                  onChange({ ...draft, sections: draft.sections.filter((_, at) => at !== i) })
                }
                path={`sections.${i}`}
                field={field}
                questionsError={error(`sections.${i}.questions`)}
              />
            )}
          </SortableItem>
        ))}
      </SortableList>

      {!readOnly && (
        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="quiet"
            disabled={draft.sections.length >= 20}
            onClick={() => onChange({ ...draft, sections: [...draft.sections, blankSection()] })}
          >
            Add section
          </Button>
          {tooManySections && <FieldError>{tooManySections}</FieldError>}
        </div>
      )}
    </div>
  );
}

function SectionBlock({
  section,
  index,
  handle,
  readOnly,
  onChange,
  onRemove,
  path,
  field,
  questionsError,
}: {
  section: SectionDraft;
  index: number;
  /** Drags the section into a new place, from `SortableItem`. */
  handle: ReactNode;
  readOnly: boolean;
  onChange: (next: SectionDraft) => void;
  onRemove: () => void;
  /** `sections.2` — where this section sits in the draft. */
  path: string;
  field: FieldBinder;
  /** The section has no questions, once that should show. */
  questionsError?: string;
}) {
  const questionsErrorId = useId();
  return (
    <section
      aria-invalid={questionsError ? true : undefined}
      aria-describedby={questionsError ? questionsErrorId : undefined}
      className="flex flex-col gap-4 rounded-2xl border border-brand-edge/35 bg-brand-wash/40 p-5"
    >
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {handle}
          <h3 className="m-0 font-mono text-sm font-semibold tracking-wider text-brand-text uppercase">
            {sectionName(section, index)}
          </h3>
        </div>
        {!readOnly && (
          <Button variant="danger" onClick={onRemove}>
            Remove section
          </Button>
        )}
      </header>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Section title" required {...field(`${path}.title`)}>
          <input
            className={inputCls}
            value={section.title}
            maxLength={200}
            disabled={readOnly}
            placeholder="About you"
            onChange={(e) => onChange({ ...section, title: e.target.value })}
          />
        </Field>
        <Field label="Description" hint="Optional." {...field(`${path}.description`)}>
          <input
            className={inputCls}
            value={section.description}
            maxLength={1000}
            disabled={readOnly}
            onChange={(e) => onChange({ ...section, description: e.target.value })}
          />
        </Field>
      </div>
      <QuestionList
        questions={section.questions}
        readOnly={readOnly}
        onChange={(questions) => onChange({ ...section, questions })}
        empty="No questions in this section yet."
        path={`${path}.questions`}
        field={field}
      />
      {questionsError && <FieldError id={questionsErrorId}>{questionsError}</FieldError>}
    </section>
  );
}

function QuestionList({
  questions,
  readOnly,
  onChange,
  empty,
  path,
  field,
}: {
  questions: QuestionDraft[];
  readOnly: boolean;
  onChange: (next: QuestionDraft[]) => void;
  empty: string;
  /** Where the list sits in the draft: `ungrouped`, or `sections.1.questions`. */
  path: string;
  field: FieldBinder;
}) {
  return (
    <div className="flex flex-col gap-3">
      {questions.length === 0 && <p className="m-0 text-base text-ink-muted">{empty}</p>}
      <SortableList
        items={questions}
        idOf={(q) => q.key}
        nameOf={(q, i) => `Question ${i + 1}${q.label.trim() ? `, ${q.label.trim()}` : ''}`}
        onReorder={onChange}
      >
        {questions.map((q, i) => (
          <SortableItem
            key={q.key}
            id={q.key}
            label={`Drag to reorder question ${i + 1}`}
            disabled={readOnly}
          >
            {(handle) => (
              <QuestionRow
                question={q}
                index={i}
                handle={handle}
                readOnly={readOnly}
                onChange={(next) => onChange(questions.map((x, at) => (at === i ? next : x)))}
                onRemove={() => onChange(questions.filter((_, at) => at !== i))}
                path={`${path}.${i}`}
                field={field}
              />
            )}
          </SortableItem>
        ))}
      </SortableList>
      {!readOnly && (
        <div>
          <Button variant="quiet" onClick={() => onChange([...questions, blankQuestion()])}>
            Add question
          </Button>
        </div>
      )}
    </div>
  );
}

function QuestionRow({
  question: q,
  index,
  handle,
  readOnly,
  onChange,
  onRemove,
  path,
  field,
}: {
  question: QuestionDraft;
  index: number;
  /** Drags the question into a new place, from `SortableItem`. */
  handle: ReactNode;
  readOnly: boolean;
  onChange: (next: QuestionDraft) => void;
  onRemove: () => void;
  path: string;
  field: FieldBinder;
}) {
  const choice = isChoiceType(q.type);
  // The question itself is labelled by `aria-label` alone, so it cannot sit
  // in a Field; its error is drawn the same way underneath.
  const label = field(`${path}.label`);
  const labelErrorId = useId();
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-hairline bg-surface p-4">
      <div className="flex items-start gap-3">
        {handle}
        <div className="grid flex-1 gap-3 sm:grid-cols-[1fr_16rem]">
          <input
            className={inputCls}
            value={q.label}
            maxLength={300}
            disabled={readOnly}
            placeholder="What would you like to ask?"
            aria-label={`Question ${index + 1}`}
            aria-invalid={label.error ? true : undefined}
            aria-describedby={label.error ? labelErrorId : undefined}
            onBlur={label.onBlur}
            onChange={(e) => onChange({ ...q, label: e.target.value })}
          />
          <Select
            variant="field"
            className={selectCls}
            value={q.type}
            onChange={(type) => onChange({ ...q, type: type as FormQuestionType })}
            options={TYPE_OPTIONS}
            disabled={readOnly}
            aria-label={`Question ${index + 1} type`}
          />
        </div>
      </div>
      {label.error && <FieldError id={labelErrorId}>{label.error}</FieldError>}
      {choice && (
        <Field
          label="Options"
          hint="One per line, two to twenty."
          required
          {...field(`${path}.options`)}
        >
          <textarea
            rows={3}
            className={inputCls}
            value={q.options}
            disabled={readOnly}
            placeholder={'Excellent\nGood\nNeeds work'}
            onChange={(e) => onChange({ ...q, options: e.target.value })}
          />
        </Field>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-base">
          <input
            type="checkbox"
            className="h-5 w-5 accent-brand-edge"
            checked={q.required}
            disabled={readOnly}
            onChange={(e) => onChange({ ...q, required: e.target.checked })}
          />
          Required
        </label>
        {!readOnly && (
          <>
            <span className="flex-1" />
            <Button variant="quiet" onClick={onRemove} aria-label={`Remove question ${index + 1}`}>
              Remove
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
